import { IValidatable, SaveChangesService } from './../services/save-changes.service';
import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { arrowClockwise } from '@icons';
import { EnumerationService } from './enumeration.service';
import { FacetView, IFacet } from '@common/facet';
import { Entity, EnumerationClass, EnumerationItem, Output } from '@common/types';
import type { Input as EnumerationInput } from '@common/types/models/input.interface'
import { DataContextService } from '@services/data-context.service';
import { Subscription } from 'rxjs';


@Component({
    selector: 'enumerations-facet',
    templateUrl: './enumerations-facet.component.html',
    styleUrls: ['./enumerations-facet.component.scss'],
})
export class EnumerationsFacetComponent implements OnInit, OnDestroy, IValidatable {
    @Input() facetId: string;
    @Input() facet: IFacet;
    @Input() facetView: FacetView;

    @ViewChild('enumerationsPanelBody') enumerationsPanelBody: ElementRef;

    readonly icons = { arrowClockwise };

    isEditing = true;
    enumerationClasses: Entity<EnumerationClass & { isSelected?: boolean }>[] = [];
    newEnumerationClasses: Entity<EnumerationClass & { isSelected?: boolean }>[] = [];

    selectedClass: Entity<EnumerationClass> = null;
    enumerationItems: Entity<EnumerationItem>[] = [];
    dangerMessage: string = null;

    private focusFirstEnumerationItem = false;

    readonly COMPONENT_LOG_TAG = 'enumerations-facet';

    constructor(
        private saveChangesService: SaveChangesService,
        private enumerationService: EnumerationService,
        private dataContextService: DataContextService
    ) {
        this.refreshData();
    }

    private subs: Subscription = new Subscription();

    ngOnInit(): void {
        this.saveChangesService.registerValidator(this);

        this.subs.add(this.dataContextService.onCancel$.subscribe(() => {
            if (this.newEnumerationClasses.indexOf(this.selectedClass) !== -1) {
                this.selectedClass = null
            }

            this.newEnumerationClasses = []
        }));

        this.subs.add(this.dataContextService.changesSaved$.subscribe(() => {
            this.newEnumerationClasses = [];
            this.refreshData();
        }));
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    selectClass(item: Entity<EnumerationClass & { isSelected?: boolean }>): void {
        this.selectedClass = item;
        this.enumerationItems = [];
        this.enumerationItems = item.EnumerationItem;

        for (const enumClass of this.enumerationClasses) {
            enumClass.isSelected = false;
        }

        for (const enumClass of this.newEnumerationClasses) {
            enumClass.isSelected = false;
        }

        item.isSelected = true;

        this.sortEnumerationItemsByReverseKey();
        this.scrollToTop();
    }

    private scrollToTop() {
        if (this.enumerationsPanelBody && this.enumerationsPanelBody.nativeElement) {
            jQuery(this.enumerationsPanelBody.nativeElement).animate({ scrollTop: 0 }, "fast");
        }
    }

    private sortEnumerationItemsByReverseKey() {
        // sorts items from most recently added to least recently added
        this.enumerationItems.sort((firstEnum, secondEnum) => {
            const a = firstEnum.C_EnumerationItem_key;
            const b = secondEnum.C_EnumerationItem_key;

            if (a < b) {
                return 1;
            }
            if (a > b) {
                return -1;
            }

            return 0;
        });
    }

    refreshData(): void {
        this.enumerationService.getEnumerationClasses().then((data: any[]) => {
            this.enumerationClasses = data;
        });
    }

    classRemoved(enumerationClass: Entity<EnumerationClass>): void {
        if (this.selectedClass === enumerationClass) {
            this.selectedClass = null;
        }
    }

    removeItem(enumerationItem: Entity<EnumerationItem>): void {
        this.enumerationService.deleteItem(enumerationItem);
    }

    addNewItem(): void {
        const initialValues = {
            C_EnumerationClass_key: this.selectedClass.C_EnumerationClass_key
        };
        this.enumerationService.createItem(initialValues);

        this.enumerationItems.unshift(this.enumerationItems.pop());

        this.focusFirstEnumerationItem = true;
    }

    addNewClass(): void {
        const newClass = this.enumerationService.createClass();
        this.newEnumerationClasses.push(newClass);
        this.selectClass(newClass);
    }

    @ViewChild('enumerationItemInput')
    set enumerationItemInput(enumItem: ElementRef) {
        // focus the first enumeration item
        if (enumItem && this.focusFirstEnumerationItem) {
            enumItem.nativeElement.focus();
            this.focusFirstEnumerationItem = false;
        }
    }

    async onSaveEnumerations(): Promise<void> {
        await this.saveChangesService.saveChanges(this.COMPONENT_LOG_TAG);
    }

    async validate(): Promise<string> {
        const haveChangedClassesWithoutName =
            this.enumerationClasses.some(
                (enumClass) => !enumClass.entityAspect.entityState.isUnchanged() && !enumClass.ClassName,
            );
        const haveChangedNewClassesWithoutName =
            this.newEnumerationClasses.some(
                (enumClass) => !enumClass.entityAspect.entityState.isUnchanged() && !enumClass.ClassName,
            );
        const haveChangedItemsWithoutClassName =
            this.enumerationItems.some(
                (enumItem) => !enumItem.entityAspect.entityState.isUnchanged() && !enumItem.EnumerationClass.ClassName,
            );

        if (haveChangedClassesWithoutName || haveChangedNewClassesWithoutName || haveChangedItemsWithoutClassName) {
            return 'Enumeration requires a Name.';
        }

        return '';
    }
}
